// ANANT SMART MARKETING — Public no-auth viral card at /asm/:slug
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AnantSmartMarketingPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [greeted, setGreeted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/asm/public/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Card not found'));
  }, [slug]);

  useEffect(() => {
    if (!data || greeted) return;
    const p = data.profile;
    // Try audio first (needs a user gesture on iOS, but many mobile browsers allow auto with mute-then-unmute)
    if (p.voice_greeter_url && audioRef.current) {
      const src = p.voice_greeter_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${p.voice_greeter_url}` : p.voice_greeter_url;
      audioRef.current.src = src;
      audioRef.current.play().catch(() => {});
    } else if (p.voice_greeter_text && 'speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(p.voice_greeter_text); u.lang = 'hi-IN';
      setTimeout(() => window.speechSynthesis.speak(u), 800);
    }
    setGreeted(true);
  }, [data, greeted]);

  const track = (kind) => axios.post(`${API}/asm/public/${slug}/track?kind=${kind}`).catch(() => {});

  const downloadVcard = () => {
    const p = data.profile;
    const vcf = [
      'BEGIN:VCARD', 'VERSION:3.0',
      `FN:${p.business_name}`,
      p.owner_name ? `N:${p.owner_name}` : '',
      p.phone ? `TEL;TYPE=CELL:${p.phone}` : '',
      p.whatsapp_number ? `TEL;TYPE=WhatsApp:${p.whatsapp_number}` : '',
      p.email ? `EMAIL:${p.email}` : '',
      p.address ? `ADR;TYPE=WORK:;;${p.address};${p.city || ''};;;` : '',
      p.website_url ? `URL:${p.website_url}` : '',
      p.tagline ? `NOTE:${p.tagline}` : '',
      'END:VCARD',
    ].filter(Boolean).join('\n');
    const blob = new Blob([vcf], { type: 'text/vcard' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${p.business_name}.vcf`; a.click();
    track('save'); toast.success('📇 Card saved');
  };

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center"><div><div className="text-2xl font-black">🚫 {err}</div><Link to="/" className="text-cyan-400 underline text-sm mt-3 block">Go to Anant Web Portal</Link></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center opacity-70">⏳ Loading…</div>;
  const p = data.profile;
  const c = p.theme_color || '#0ea5e9';

  return (
    <div className="min-h-screen text-white pb-16" style={{ background: `linear-gradient(180deg, ${c}22, #020617 60%)` }} data-testid="asm-public-card">
      <audio ref={audioRef} className="hidden" />

      {/* Festive banner */}
      {p.festive_banner_url && (
        <div className="w-full aspect-[3/1] bg-cover bg-center animate-pulse" style={{ backgroundImage: `url(${p.festive_banner_url})` }} data-testid="asm-fest-banner" />
      )}
      {p.festive_message && (
        <div className="text-center py-2 bg-gradient-to-r from-amber-500/30 to-pink-500/30 text-amber-100 text-xs font-black">🎉 {p.festive_message}</div>
      )}

      <div className="max-w-md mx-auto px-4 pt-6">
        {/* Cover */}
        {p.cover_url && <div className="aspect-video bg-cover bg-center rounded-2xl mb-3" style={{ backgroundImage: `url(${p.cover_url})` }} />}

        {/* Logo + name */}
        <div className="flex items-center gap-3 mb-3">
          {p.logo_url && <div className="w-20 h-20 rounded-2xl bg-cover bg-center border-2 border-white/20" style={{ backgroundImage: `url(${p.logo_url})` }} />}
          <div className="flex-1">
            <h1 className="text-2xl font-black leading-tight">{p.business_name}</h1>
            {p.owner_name && <div className="text-sm opacity-80">{p.owner_name}</div>}
            {p.tagline && <div className="text-[11px] opacity-70 italic mt-0.5">{p.tagline}</div>}
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {p.phone && <a href={`tel:${p.phone}`} onClick={() => track('click_call')} data-testid="asm-btn-call" className="px-3 py-2.5 rounded-xl bg-cyan-500 text-black text-sm font-black text-center">📞 Call</a>}
          {p.whatsapp_number && <a href={`https://wa.me/${p.whatsapp_number.replace(/[^\d]/g,'')}`} onClick={() => track('click_wa')} target="_blank" rel="noreferrer" data-testid="asm-btn-wa" className="px-3 py-2.5 rounded-xl bg-green-500 text-black text-sm font-black text-center">💬 WhatsApp</a>}
          {(p.latitude && p.longitude) && <a href={`https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`} onClick={() => track('click_map')} target="_blank" rel="noreferrer" data-testid="asm-btn-map" className="px-3 py-2.5 rounded-xl bg-amber-500 text-black text-sm font-black text-center">🗺️ Directions</a>}
          <button onClick={downloadVcard} data-testid="asm-btn-save" className="px-3 py-2.5 rounded-xl bg-fuchsia-500 text-white text-sm font-black">📇 Save Card</button>
          {p.website_url && <a href={p.website_url} target="_blank" rel="noreferrer" className="col-span-2 px-3 py-2 rounded-xl bg-slate-700 text-white text-sm font-black text-center">🌐 {p.website_url.replace(/https?:\/\//,'')}</a>}
        </div>

        {/* Address */}
        {p.address && <div className="text-[11px] opacity-80 mb-3">📍 {p.address}{p.city ? `, ${p.city}` : ''}</div>}

        {/* Catalog / mini-store */}
        {data.catalog?.length > 0 && (
          <div className="mb-4" data-testid="asm-mini-store">
            <div className="text-[10px] uppercase tracking-widest opacity-70 font-black mb-2">🛍️ Catalog · {data.catalog.length} items</div>
            <div className="grid grid-cols-2 gap-2">
              {data.catalog.map((it) => {
                const waText = encodeURIComponent(`Hi ${p.business_name}, I'm interested in ${it.name} ${it.price_inr ? `(₹${it.price_inr})` : ''}`);
                const orderUrl = p.whatsapp_number ? `https://wa.me/${p.whatsapp_number.replace(/[^\d]/g,'')}?text=${waText}` : null;
                return (
                  <a key={it.id} href={orderUrl || '#'} onClick={() => orderUrl && track('click_wa')} target="_blank" rel="noreferrer"
                     data-testid={`asm-store-item-${it.id}`} className="rounded-xl border border-white/15 bg-slate-900/60 p-2 hover:border-fuchsia-400 transition">
                    {it.image_url && <div className="aspect-square bg-cover bg-center rounded mb-1" style={{ backgroundImage: `url(${it.image_url})` }} />}
                    <div className="text-[11px] font-black truncate">{it.name}</div>
                    {it.description && <div className="text-[9px] opacity-70 truncate">{it.description}</div>}
                    <div className="text-[11px] font-black text-emerald-300 mt-0.5">{it.price_inr ? `₹${it.price_inr}` : ''} {it.price_at ? `· ${it.price_at} AT` : ''}</div>
                    {orderUrl && <div className="text-[9px] text-green-300 mt-0.5">📱 Order on WhatsApp</div>}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Social */}
        {p.social && Object.keys(p.social).length > 0 && (
          <div className="flex gap-2 mb-3 justify-center">
            {p.social.ig && <a href={p.social.ig} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-pink-500 flex items-center justify-center text-white">📷</a>}
            {p.social.fb && <a href={p.social.fb} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white">📘</a>}
            {p.social.yt && <a href={p.social.yt} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-red-500 flex items-center justify-center text-white">▶️</a>}
            {p.social.x && <a href={p.social.x} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-slate-900 flex items-center justify-center text-white">𝕏</a>}
          </div>
        )}

        {/* Share button */}
        <button onClick={async () => {
          const url = window.location.href;
          const shareText = `${p.business_name}${p.tagline ? ' · ' + p.tagline : ''}\n${url}`;
          try {
            if (navigator.share) { await navigator.share({ title: p.business_name, text: shareText, url }); track('share'); }
            else { navigator.clipboard.writeText(url); toast.success('Link copied'); track('share'); }
          } catch { /* user cancelled */ }
        }} data-testid="asm-btn-share" className="w-full px-3 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white text-sm font-black">↗ Share This Card</button>

        {/* Viral footer */}
        {data.show_brand_footer && (
          <Link to="/" data-testid="asm-brand-footer" className="mt-6 block text-center text-[10px] opacity-70 hover:opacity-100">
            Powered by <b>Anant Web Portal</b> · Get your free digital card
          </Link>
        )}
      </div>
    </div>
  );
}
